import {
  createFileRoute,
  Link,
  useNavigate,
  redirect,
} from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { passwordSchema } from '@/schemas/auth'

// Form schema for reset password (without token field since it's handled separately)
const resetPasswordFormSchema = z
  .object({
    password: passwordSchema,
    passwordConfirm: z.string(),
  })
  .refine(data => data.password === data.passwordConfirm, {
    message: "Passwords don't match",
    path: ['passwordConfirm'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordFormSchema>
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState, useEffect, ComponentProps } from 'react'
import { Eye, EyeOff, Loader2, Brain, CheckCircle, X } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'
import { getPasswordStrength } from '@/lib/utils'

function PasswordStrengthIndicator({ password }: { password: string }) {
  const { score, feedback } = getPasswordStrength(password)

  const getStrengthColor = (score: number) => {
    if (score <= 1) return 'bg-red-500'
    if (score <= 3) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getStrengthText = (score: number) => {
    if (score <= 1) return 'Weak'
    if (score <= 3) return 'Medium'
    return 'Strong'
  }

  if (!password) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Password strength</span>
        <span
          className={`text-xs font-medium ${
            score <= 1
              ? 'text-red-500'
              : score <= 3
                ? 'text-yellow-500'
                : 'text-green-500'
          }`}
        >
          {getStrengthText(score)}
        </span>
      </div>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(level => (
          <div
            key={level}
            className={`h-1 w-full rounded-full ${
              level <= score ? getStrengthColor(score) : 'bg-muted'
            }`}
          />
        ))}
      </div>
      {feedback.length > 0 && (
        <div className="space-y-1">
          {feedback.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <X className="h-3 w-3 text-red-500" />
              <span className="text-xs text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ResetPasswordForm({
  className,
  token,
  ...props
}: ComponentProps<'div'> & { token: string }) {
  const navigate = useNavigate()
  const { resetPassword, isLoading, error, validationErrors, resetAuthState } =
    useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  // Reset auth state when component mounts (handles back navigation from OAuth)
  useEffect(() => {
    resetAuthState()
  }, [resetAuthState])

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordFormSchema),
  })

  const password = watch('password')

  const onSubmit = async (data: ResetPasswordFormData) => {
    console.log('Form submitted with data:', data)
    console.log('Token:', token)
    console.log('Form validation passed, attempting API call...')
    try {
      await resetPassword(token, data.password, data.passwordConfirm)
      setIsSuccess(true)
    } catch {
      // Handle validation errors from the store
      const { validationErrors: currentErrors } = useAuthStore.getState()
      if (currentErrors) {
        Object.entries(currentErrors).forEach(([field, messages]) => {
          if (field === 'token') {
            // Token errors should be displayed as general errors, not field-specific
            return
          }
          setError(field as keyof ResetPasswordFormData, {
            type: 'server',
            message: messages[0],
          })
        })
      }
    }
  }

  if (isSuccess) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className="overflow-hidden p-0 bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
          <CardContent className="p-6 md:p-8">
            <div>
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                      <Brain className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-lg font-semibold">ThoughtFabric</span>
                  </div>
                  <div className="mb-4">
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                  </div>
                  <h1 className="text-2xl font-bold">
                    Password reset successful
                  </h1>
                  <p className="text-xs text-muted-foreground text-balance">
                    Password has been reset successfully. Please log in with
                    your new password.
                  </p>
                </div>

                <Button
                  onClick={() => navigate({ to: '/auth/login' })}
                  className="w-full bg-black hover:bg-black/90 text-white"
                >
                  Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0 bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
        <CardContent className="p-6 md:p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                    <Brain className="h-5 w-5 text-black" />
                  </div>
                  <span className="text-lg font-semibold">ThoughtFabric</span>
                </div>
                <h1 className="text-2xl font-bold">Reset your password</h1>
                <p className="text-xs text-muted-foreground text-balance">
                  Enter your new password below
                </p>
              </div>

              {error && (
                <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-4 py-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="grid gap-3">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    {...register('password')}
                    className={`bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500 ${
                      errors.password || validationErrors?.password
                        ? 'border-destructive'
                        : ''
                    }`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">
                    {errors.password.message}
                  </p>
                )}
                {validationErrors?.password && (
                  <p className="text-xs text-destructive">
                    {validationErrors.password[0]}
                  </p>
                )}
                <PasswordStrengthIndicator password={password || ''} />
              </div>

              <div className="grid gap-3">
                <Label htmlFor="passwordConfirm">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="passwordConfirm"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    placeholder="Confirm your new password"
                    {...register('passwordConfirm')}
                    className={`bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500 ${
                      errors.passwordConfirm ||
                      validationErrors?.passwordConfirm
                        ? 'border-destructive'
                        : ''
                    }`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    aria-label={
                      showPasswordConfirm ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPasswordConfirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.passwordConfirm && (
                  <p className="text-xs text-destructive">
                    {errors.passwordConfirm.message}
                  </p>
                )}
                {validationErrors?.passwordConfirm && (
                  <p className="text-xs text-destructive">
                    {validationErrors.passwordConfirm[0]}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-black hover:bg-black/90 text-white"
                disabled={isSubmitting || isLoading}
                onClick={e => {
                  console.log('Button clicked!', e)
                }}
              >
                {isSubmitting || isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  'Reset password'
                )}
              </Button>

              <div className="text-center text-sm">
                Remember your password?{' '}
                <Link
                  to="/auth/login"
                  className="underline underline-offset-4 hover:text-black"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-center text-xs text-balance text-gray-400">
        By continuing, you agree to our{' '}
        <a
          href="https://thoughtfabric.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://thoughtfabric.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Privacy Policy
        </a>
        .
      </div>
    </div>
  )
}

function ResetPasswordPage() {
  const { token } = Route.useSearch()

  // If no token is provided, redirect to forgot password page
  if (!token) {
    throw redirect({ to: '/auth/forgot-password' })
  }

  return (
    <BackgroundWrapper>
      <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <ResetPasswordForm token={token} />
        </div>
      </div>
    </BackgroundWrapper>
  )
}

export const Route = createFileRoute('/reset-password/')({
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  beforeLoad: async () => {
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    // If user is already authenticated, redirect to dashboard
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: ResetPasswordPage,
})
